import { ArrowUpward, PictureAsPdf } from '@mui/icons-material';
import { Box, List, ListItem, ListItemButton, ListItemText, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Autosizer, Protect, Scrollbar } from 'app/components';
import { useLocalStorage, useSingleAndDoubleClick } from 'app/services';
import React, { createContext, createRef, forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { FixedSizeList } from 'react-window';

// const tableSortFn = (a, b) => {
//     switch (typeof a[sortSelectedItem.name]) {
//         case 'name':
//             switch (sortSelectedKey.order) {
//                 case 'ASC':
//                     return a.name.localeCompare(b.name, {
//                         sensitivity: 'base',
//                     });
//                 case 'DESC':
//                     return b.name.localeCompare(a.name, {
//                         sensitivity: 'base',
//                     });
//                 default:
//                     return a - b;
//             }
//         default:
//             return;
//     }
// };

const TableContext = createContext();

const Table = ({
  columns: inputColumns,
  customColumnsRender = null,
  showColumns,
  rows,
  searchValue,
  searchValueKeys = [],
  checkedRows = [],
  selectedRow = null,
  onRowClick = () => {},
  onRowDoubleClick = () => {},
  onSortChange = () => {},
  storageColumnsWidthKey,
  itemSize = 24,
  stickyIndices = [0],
  overscanCount = 10,
}) => {
  const isResizingColumn = useRef(null);
  const [sortSelectedKey, setSortSelectedKey] = useState({
    name: inputColumns[0].name,
    order: 'DESC',
  });
  const [columnsWidth, setColumnsWidth] = useLocalStorage(storageColumnsWidthKey, {});
  const { handleItemClick: onClick, handleItemDoubleClick: onDoubleClick } = useSingleAndDoubleClick({
    onClick: (v) => onRowClick(v),
    onDoubleClick: (v) => onRowDoubleClick(v),
    timeout: 0,
  });

  const columns = useMemo(
    () =>
      inputColumns
        .filter((column) => showColumns.includes(column.name))
        .map((column) => ({
          ...column,
          width: columnsWidth[column.name] ? columnsWidth[column.name] : column.width ? column.width : column.minWidth,
        })),
    [inputColumns, columnsWidth, showColumns]
  );

  const columnsWatch = useRef(columns);
  const columnsRefs = useRef(Object.fromEntries(columns.map((column) => [column.name, createRef()])));

  const sortRows = useMemo(() => {
    const compareFn = (_a, _b) => {
      const a = _a[sortSelectedKey.name];
      const b = _b[sortSelectedKey.name];

      switch (typeof a) {
        case 'string':
          if (
            /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01]) ([01][0-9]|2[0-3]):([0-5][0-9]):([0-5][0-9])$/.test(a[sortSelectedKey.name])
          ) {
            switch (sortSelectedKey.order) {
              case 'ASC':
                return new Date(a) - new Date(b);
              case 'DESC':
                return new Date(b) - new Date(a);
            }
          } else {
            switch (sortSelectedKey.order) {
              case 'ASC':
                return a.localeCompare(b, {
                  sensitivity: 'base',
                });
              case 'DESC':
                return b.localeCompare(a, {
                  sensitivity: 'base',
                });
            }
          }
          break;
        case 'boolean':
        case 'number': {
          switch (sortSelectedKey.order) {
            case 'ASC':
              return a - b;
            case 'DESC':
              return b - a;
          }
          break;
        }
        default:
          break;
      }
    };

    return (
      searchValue.length
        ? rows.filter((row) => searchValueKeys.some((key) => row[key]?.toLowerCase().indexOf(searchValue.toLowerCase()) !== -1))
        : rows
    ).sort(compareFn);
  }, [rows, searchValue, sortSelectedKey, searchValueKeys]);

  const adjustWidthColumn = (name, width) => {
    const columnIndex = columnsWatch.current.findIndex((column) => column.name === name);
    const column = columnsWatch.current[columnIndex];
    const newWidth = width > column.maxWidth ? column.maxWidth : width < column.minWidth ? column.minWidth : width;
    const ul = columnsRefs.current[name].current?.parentElement?.parentElement?.parentElement;

    columnsRefs.current[name].current.parentElement.style.width = newWidth + 'px';
    columnsRefs.current[name].current.parentElement.style.minWidth = newWidth + 'px';

    for (var i = 0; i < ul?.children?.length; i++) {
      ul.children[i].children[columnIndex].style.width = newWidth + 'px';
      ul.children[i].children[columnIndex].style.minWidth = newWidth + 'px';
    }
  };

  const handleOnMouseMove = (e) => {
    if (isResizingColumn.current) {
      const newWidth = e.clientX - columnsRefs.current[isResizingColumn.current]?.current.parentElement?.getBoundingClientRect().left;
      adjustWidthColumn(isResizingColumn.current, newWidth);
    }
  };

  const handleOnMouseUp = () => {
    isResizingColumn.current = null;

    const columnsInfo = {};
    columns.forEach((column) => {
      const parentElement = columnsRefs.current[column.name]?.current?.parentElement;

      columnsInfo[column.name] = parentElement ? parseInt(parentElement.style.width.slice(0, -2)) : column.width;
    });

    setColumnsWidth(columnsInfo);
  };

  const onClickResizeColumn = (column, e) => {
    e.preventDefault();
    isResizingColumn.current = column.name;
  };

  const onColumnRef = (column, el) => {
    if (el && !columnsRefs.current[column.name].current) {
      columnsRefs.current[column.name].current = el;
    }
  };

  const handleSortClick = (column) => {
    const sortData = {
      name: column.name,
      order: sortSelectedKey.name !== column.name ? column.order : sortSelectedKey.order === 'ASC' ? 'DESC' : 'ASC',
    };

    setSortSelectedKey(sortData);
    onSortChange(sortData);
  };

  useEffect(() => {
    document.onmousemove = handleOnMouseMove;
    document.onmouseup = handleOnMouseUp;

    return () => {
      document.onmousemove = null;
      document.onmouseup = null;
    };
  }, []);

  useEffect(() => {
    columnsWatch.current = columns;
  }, [columns]);
  return (
    <Box
      component='div'
      sx={{
        width: '100%',
        height: '100%',
        '*:focus': {
          outline: 'none',
        },
        '.simplebar-offset > div': {
          '&::-webkit-scrollbar': {
            width: 0,
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
            boxShadow: 'none',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'transparent',
            boxShadow: 'none',
          },
        },
        '.simplebar-container': {
          maxHeight: 'none',
          '.simplebar-horizontal': {
            right: '0px',
          },
          '.simplebar-vertical': {
            top: '30px',
            bottom: '0px',
          },
        },
      }}>
      <Autosizer>
        {({ height, width }) => (
          <Box sx={{ height, width }}>
            <TableContext.Provider
              value={{
                rows: sortRows,
                width,
                height,
                columns,
                columnsRefs: columnsRefs.current,
                checkedRows,
                searchValue,
                sortSelectedKey,
                onSortClick: handleSortClick,
                onCheckboxClick: () => {},
                onClickResizeColumn,
                onColumnRef,
              }}>
              <Scrollbar>
                {({ scrollableNodeRef, contentNodeRef }) => (
                  <FixedSizeList
                    height={height}
                    innerElementType={InnerElementType}
                    itemCount={sortRows.length}
                    itemSize={itemSize}
                    stickyIndices={stickyIndices}
                    overscanCount={overscanCount}
                    innerRef={contentNodeRef}
                    outerRef={scrollableNodeRef}
                    itemData={{
                      rows: sortRows,
                      itemWidth: scrollableNodeRef?.current?.scrollWidth ?? width,
                      columns,
                      selectedRow,
                      onClick,
                      onDoubleClick,
                      onColumnRef,
                      customColumnsRender,
                    }}>
                    {RowRender}
                  </FixedSizeList>
                )}
              </Scrollbar>
            </TableContext.Provider>
          </Box>
        )}
      </Autosizer>
    </Box>
  );
};

const InnerElementType = forwardRef(({ children, style, ...rest }, ref) => (
  <TableContext.Consumer>
    {({ onSortClick, onClickResizeColumn, sortSelectedKey, columns, onColumnRef }) => {
      return (
        <List ref={ref} style={{ ...style, minHeight: '100%' }} {...rest}>
          <ListItem
            disablePadding
            sx={(theme) => ({
              position: 'absolute',
              top: '0',
              left: '0',
              width: '100%',
              height: style.height,
              minHeight: '100%',
              zIndex: '2',
              pointerEvents: 'none',
              padding: theme.spacing(0),
              '.MuiListItemText-root': {
                display: 'flex',
                justifyContent: 'space-between',
                height: '100%',
                position: 'relative',
                flex: 'none',
                '&:before': {
                  content: '""',
                  position: 'absolute',
                  top: '0',
                  right: '0',
                  height: '100%',
                  bgcolor: theme.palette.divider,
                  width: '1px',
                  zIndex: '1',
                  pointerEvents: 'none',
                },
                '&:last-of-type': {
                  flex: '1',
                  '&:before': {
                    display: 'none',
                  },
                },
              },
            })}>
            {columns.map((column) => (
              <Protect
                key={column.name}
                permissions={column.permissions}
                renderPermitted={() => (
                  <ListItemText
                    style={{
                      minWidth: column.width ? column.width : column.minWidth,
                    }}
                  />
                )}
                renderForbidden={() => <React.Fragment />}
              />
            ))}
          </ListItem>
          <ListItem
            disablePadding
            sx={(theme) => ({
              position: 'sticky',
              top: '0',
              width: '100%',
              display: 'flex',
              zIndex: '2',
              '& > .MuiListItemButton-root': {
                '&:first-of-type': {
                  '.MuiListItemText-root': {
                    paddingLeft: '31px',
                  },
                },
              },
              '& > .MuiBox-root': {
                '&:last-of-type': {
                  flex: '1',
                  '.MuiListItemText-root': {
                    paddingRight: theme.spacing(2),
                  },
                },
              },
              '.MuiListItemButton-root': {
                display: 'flex',
                flex: 'none',
                height: '30px',
                padding: theme.spacing(0),
                position: 'relative',
                bgcolor: 'background.default',
                borderColor: theme.palette.divider,
                '.MuiTouchRipple-rippleVisible': {
                  color: 'inherit',
                },
                '.MuiListItemText-root': {
                  display: 'flex',
                  cursor: 'pointer',
                  margin: theme.spacing(0),
                  padding: '6px 6px',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  svg: {
                    fontSize: '14px',
                  },
                  '.MuiTypography-root': {
                    display: 'flex',
                    '&:nth-of-type(1)': {
                      color: 'text.secondary',
                      marginRight: '4px',
                    },
                    '&:nth-of-type(2) svg': {
                      fontSize: '12px',
                      transition: 'all 300ms',
                      transform: 'rotateZ(0deg)',
                    },
                  },
                  '.MuiCheckbox-root': {
                    padding: theme.spacing(0),
                    marginLeft: '-2px',
                    svg: {
                      fontSize: '18px',
                    },
                  },
                },
              },
            })}>
            {columns.map((column) => (
              <Protect
                key={column.name}
                permissions={column.permissions}
                renderForbidden={() => <React.Fragment />}
                renderPermitted={() => (
                  <Box
                    component='div'
                    sx={{
                      display: 'flex',
                      position: 'relative',
                      minWidth: column.width ? column.width : column.minWidth,
                    }}>
                    <ListItemButton
                      onClick={() => onSortClick(column)}
                      disableTouchRipple={!column.order}
                      sx={(theme) => ({
                        flex: '1 1 auto !important',
                        '.MuiTypography-root': {
                          width: 'auto',
                          '&:nth-of-type(2)': {
                            opacity: '0.2',
                            transform: 'rotateZ(0)',
                            transition:
                              'transform ' + theme.transitions.duration.shortest + 'ms ' + theme.transitions.easing.easeInOut + ' 0ms',
                            ...(sortSelectedKey.name === column.name && {
                              opacity: '1',
                              ...(sortSelectedKey.order === 'DESC' && {
                                transform: 'rotateZ(180deg)',
                              }),
                            }),
                          },
                        },
                      })}>
                      {column.name == 'download' ? (
                        <ListItemText
                          disableTypography
                          sx={{
                            flex: '1 1 auto',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            minHeight: 30,
                          }}>
                          <PictureAsPdf fontSize='small' sx={{ color: 'text.secondary' }} titleAccess={column.label} />
                        </ListItemText>
                      ) : (
                        <ListItemText disableTypography>
                          <Typography>{column.label}</Typography>
                          <Typography>{column.order && <ArrowUpward fontSize='small' />}</Typography>
                        </ListItemText>
                      )}
                    </ListItemButton>
                    <Box
                      component='div'
                      ref={(el) => onColumnRef(column, el)}
                      onMouseDown={(e) => onClickResizeColumn(column, e)}
                      sx={(theme) => ({
                        minWidth: '4px',
                        minHeight: '30px',
                        cursor: 'col-resize',
                        backgroundColor: theme.palette.background.default,
                        '&:hover, &:active': {
                          backgroundColor: alpha(theme.palette.text.secondary, 0.12),
                        },
                      })}
                    />
                  </Box>
                )}
              />
            ))}
          </ListItem>
          {children}
        </List>
      );
    }}
  </TableContext.Consumer>
));

const RowRender = ({ data, index, style }) => {
  const {
    rows,
    selectedRow,
    onClick,
    onDoubleClick,

    // eslint-disable-next-line no-unused-vars
    onCheckboxClick,
    // eslint-disable-next-line no-unused-vars
    checkedRows = [],
    columns,
    customColumnsRender,
  } = data;

  return (
    <ListItem
      key={index}
      style={style}
      selected={selectedRow?.id === rows[index].id}
      onClick={() => onClick(rows[index])}
      onDoubleClick={() => onDoubleClick(rows[index])}
      disablePadding
      sx={(theme) => ({
        width: '100%',
        marginTop: '30px',
        userSelect: 'none',
        borderRadius: '0',
        '&:hover': {
          '.MuiListItemText-root': {
            cursor: 'pointer',
          },
        },
        '&.Mui-selected': {
          '.MuiListItemText-root': {
            bgcolor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
          },
          '.MuiCheckbox-root': {
            color: theme.palette.primary.contrastText,
            '&.Mui-checked': {
              color: theme.palette.primary.contrastText,
            },
          },
        },
        '.MuiListItemText-root': {
          display: 'flex',
          flex: 'none',
          height: '100%',
          margin: theme.spacing(0),
          padding: theme.spacing(0.5, 1),
          alignItems: 'center',
          overflow: 'hidden',
          '&:first-of-type': {
            paddingLeft: theme.spacing(2),
            gap: '3px',
            svg: {
              fontSize: '16px',
            },
          },
          '&:last-of-type': {
            flex: '1',
            paddingRight: theme.spacing(2),
          },
          '.MuiTypography-root': {
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
          },
          '.MuiCheckbox-root': {
            padding: theme.spacing(0),
            marginLeft: '-2px',
            svg: {
              fontSize: '18px',
            },
          },
        },
        ...((rows.length - (rows.length - 1 - index)) % 2 === 0 && {
          '& .MuiListItemText-root': {
            bgcolor: 'background.default',
          },
        }),
      })}>
      {/* <ListItemText disableTypography style={{ minWidth: '38px' }}>
                <Checkbox
                    size="small"
                    color="primary"
                    checked={checkedRows.indexOf(rows[index].id) !== -1}
                    onClick={() => onCheckboxClick(rows[index])}
                    disableRipple>
                </Checkbox>
            </ListItemText> */}
      {columns.map((column) =>
        customColumnsRender ? (
          customColumnsRender(column, rows[index])
        ) : (
          <ListItemText key={column.name} sx={{ width: column.width }}>
            <Typography>{column.format ? column.format(rows[index][column.name]) : rows[index][column.name]}</Typography>
          </ListItemText>
        )
      )}
    </ListItem>
  );
};

export default Table;
